import React, { useState, useEffect, useRef } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Github, Linkedin, Mail, ArrowRight, DownloadCloud, Sparkles, Terminal, ShieldCheck, Briefcase } from "lucide-react";
import { CONTACT, ROTATING_ROLES } from "../data/portfolioData";

export default function Hero() {
  const [roleIndex, setRoleIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const canvasRef = useRef(null);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [12, -12]), { stiffness: 150, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-12, 12]), { stiffness: 150, damping: 20 });

  function handleMouseMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    const mouseXPos = e.clientX - rect.left;
    const mouseYPos = e.clientY - rect.top;

    const xPct = mouseXPos / width - 0.5;
    const yPct = mouseYPos / height - 0.5;

    mouseX.set(xPct);
    mouseY.set(yPct);
  }

  function handleMouseLeave() {
    mouseX.set(0);
    mouseY.set(0);
  }

  // Typewriter effect
  useEffect(() => {
    const fullRole = ROTATING_ROLES[roleIndex];
    const typingSpeed = isDeleting ? 40 : 80;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setCurrentText(fullRole.substring(0, currentText.length + 1));
        if (currentText === fullRole) {
          setTimeout(() => setIsDeleting(true), 1800);
        }
      } else {
        setCurrentText(fullRole.substring(0, currentText.length - 1));
        if (currentText === "") {
          setIsDeleting(false);
          setRoleIndex((prev) => (prev + 1) % ROTATING_ROLES.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, roleIndex]);

  // Particle Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    let width = (canvas.width = canvas.parentElement.offsetWidth);
    let height = (canvas.height = canvas.parentElement.offsetHeight);

    const handleResize = () => {
      if (!canvas || !canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;
    };

    window.addEventListener("resize", handleResize);

    const particles = Array.from({ length: 40 }).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * 2 + 1,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.4 + 0.2,
    }));

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99, 102, 241, ${p.alpha})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <section id="hero" className="relative min-h-screen pt-28 pb-16 flex items-center overflow-hidden bg-gradient-to-b from-indigo-50/60 via-slate-50 to-white">
      {/* Canvas Floating Particles */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <canvas ref={canvasRef} className="w-full h-full" />
      </div>

      {/* Background Soft Blobs */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-indigo-200/50 via-purple-200/40 to-cyan-200/40 rounded-full blur-[120px] pointer-events-none animate-pulse-glow" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left Column: Greeting & Rotating Titles */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-7 space-y-8 text-center lg:text-left"
        >
          {/* Greeting Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-indigo-200 shadow-sm"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-xs font-mono font-bold text-slate-800 uppercase tracking-wider">
              Available for Full-Time Software Engineering Roles
            </span>
          </motion.div>

          {/* Main Title & Typewriter */}
          <div className="space-y-4">
            <div className="text-xl sm:text-2xl font-bold text-indigo-700 tracking-tight">
              Hello 👋 I'm
            </div>
            <h1 className="text-4xl sm:text-6xl xl:text-7xl font-black tracking-tight text-slate-900 leading-[1.08]">
              Rithwik Srivastav
            </h1>

            {/* Rotating Titles */}
            <div className="h-12 flex items-center justify-center lg:justify-start">
              <div className="font-mono text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Terminal className="text-indigo-600 shrink-0" size={24} />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 to-purple-700 font-extrabold">
                  {currentText}
                </span>
                <span className="w-2 h-7 bg-indigo-600 animate-pulse rounded-sm inline-block" />
              </div>
            </div>
          </div>

          {/* High-Contrast Bio */}
          <p className="text-base sm:text-lg text-slate-800 font-medium max-w-2xl leading-relaxed mx-auto lg:mx-0">
            Versatile <span className="text-slate-900 font-extrabold underline decoration-indigo-300">Software Engineer</span> & Full Stack Developer building robust web platforms (<span className="text-slate-900 font-bold">React, Python Django, Drupal CMS</span>) combined with enterprise Cloud & DevOps infrastructure (<span className="text-slate-900 font-bold">AWS, Azure, Docker, Kubernetes, Terraform</span>).
          </p>

          {/* Call to Action Buttons */}
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3.5 pt-2">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-extrabold text-sm text-white bg-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-600/30 hover:scale-[1.02] active:scale-[0.98] transition-all border border-indigo-500"
            >
              <Briefcase size={17} />
              <span>Hire Me</span>
            </a>

            <a
              href="#projects"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm text-indigo-900 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition-all hover:scale-[1.02]"
            >
              <Sparkles size={17} className="text-indigo-600" />
              <span>View Projects</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>

            <a
              href={CONTACT.resume}
              download
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm text-slate-800 bg-white hover:bg-slate-50 border border-slate-300 shadow-sm transition-all hover:scale-[1.02]"
            >
              <DownloadCloud size={17} className="text-indigo-600" />
              <span>Resume PDF</span>
            </a>
          </div>

          {/* Social Links Row */}
          <div className="flex items-center justify-center lg:justify-start gap-4 pt-4 border-t border-slate-200">
            <span className="text-xs font-mono font-bold text-slate-700">Connect:</span>
            {[
              { icon: Github, href: CONTACT.github, label: "GitHub" },
              { icon: Linkedin, href: CONTACT.linkedin, label: "LinkedIn" },
              { icon: Mail, href: `mailto:${CONTACT.email}`, label: "Email" },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                className="p-2.5 rounded-xl bg-white text-slate-800 hover:text-indigo-600 border border-slate-200 hover:border-indigo-400 hover:scale-110 transition-all shadow-sm"
              >
                <s.icon size={18} />
              </a>
            ))}
          </div>
        </motion.div>

        {/* Right Column: 3D Tilt Profile Card with Animated Ring */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="lg:col-span-5 flex justify-center"
        >
          <motion.div
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative group w-full max-w-[340px] sm:max-w-[380px] aspect-square rounded-3xl p-1 cursor-pointer"
          >
            {/* Animated Rotating Gradient Ring */}
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-500 opacity-70 blur-md group-hover:opacity-100 group-hover:blur-lg transition duration-500 animate-spin-slow" />

            {/* Profile Card Container */}
            <div className="relative h-full w-full rounded-[22px] bg-white p-2 overflow-hidden border border-slate-200 shadow-xl flex flex-col justify-between">
              <div className="relative w-full h-full rounded-2xl overflow-hidden bg-slate-100">
                <img
                  src={CONTACT.profileImage}
                  alt={CONTACT.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />

                {/* Badge Overlay */}
                <div className="absolute bottom-3 left-3 right-3 p-3 rounded-xl bg-white/95 backdrop-blur-md border border-slate-200 shadow-md flex items-center justify-between">
                  <div>
                    <div className="text-xs font-black text-slate-900">{CONTACT.name}</div>
                    <div className="text-[11px] text-indigo-700 font-mono font-bold">Senior Software Engineer</div>
                  </div>
                  <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <ShieldCheck size={16} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
